import { FeatureFlagService } from 'features/feature-toggle/services';
import { AST_SIGNMAIL_MODE_ENABLED } from 'features/ast-signmail/constants';
import { GreetingPreference } from 'features/signmail/enums';

export interface AstSignMailFlag {
  enabled?: boolean;
  /**
   * When true, emergency callbacks may use AST if the other gates pass.
   * Emergency calls stay on regular SignMail even when this is true.
   */
  emergencyCallbacksEnabled?: boolean;
  greetings?: string[];
  allowedLanguages?: string[];
  initialCallOnly?: boolean;
}

export interface AstSignMailRoutingContext {
  hasDeafConnected: boolean;
  isMailboxFull?: boolean;
  isEmergencyCall?: boolean;
  isEmergencyCallback?: boolean;
}

export const AST_SIGN_MAIL_BLOCKING_REASON = {
  FEATURE_FLAG_DISABLED: 'AST SignMail feature flag is disabled',
  GREETING_NOT_ALLOWED: 'Greeting preference is not allowed for AST SignMail',
  LANGUAGE_NOT_ALLOWED: 'Call language is not allowed for AST SignMail',
  CORE_MODE_DISABLED: 'Core has disabled AST SignMail mode',
  INITIAL_CALL_ONLY_POLICY:
    'Deaf endpoint already connected while initial-call-only policy is enabled',
  MAILBOX_FULL: 'SignMail mailbox is full',
  EMERGENCY_CALL: 'Emergency calls cannot route to AST SignMail',
  EMERGENCY_CALLBACK_DISABLED:
    'Emergency callback routing to AST SignMail is disabled',
} as const;

export type AstSignMailBlockingReason =
  (typeof AST_SIGN_MAIL_BLOCKING_REASON)[keyof typeof AST_SIGN_MAIL_BLOCKING_REASON];

export interface AstSignMailEligibilityChecks {
  isFeatureFlagEnabled: boolean;
  isGreetingAllowed: boolean;
  isLanguageAllowed: boolean;
  isAstSignMailModeEnabled: boolean;
  isAllowedByInitialCallOnlyPolicy: boolean;
  isMailboxFull: boolean;
  isBlockedByEmergencyCall: boolean;
  isBlockedByEmergencyCallback: boolean;
}

export interface AstSignMailEligibility {
  isAstEligible: boolean;
  blockingReasons: AstSignMailBlockingReason[];
  astSignmailFlag: AstSignMailFlag | undefined;
  greetingPreferenceKey: string | undefined;
  isGreetingAllowed: boolean;
  isLanguageAllowed: boolean;
  checks: AstSignMailEligibilityChecks;
}

const CANONICAL_LANGUAGE_CODE_BY_VALUE: Record<string, string> = {
  en: 'en',
  english: 'en',
  es: 'es',
  spanish: 'es',
};

export const normalizeGreetingPreference = (
  greetingPreference: string | number | undefined
): GreetingPreference | undefined => {
  if (greetingPreference === undefined || greetingPreference === null) {
    return undefined;
  }

  const asString = String(greetingPreference);

  if (
    Object.values(GreetingPreference).includes(asString as GreetingPreference)
  ) {
    return asString as GreetingPreference;
  }

  if (asString in GreetingPreference) {
    return GreetingPreference[asString as keyof typeof GreetingPreference];
  }

  return undefined;
};

export const resolveGreetingPreferenceFlagKey = (
  greetingPreference: GreetingPreference
): string | undefined => {
  return Object.entries(GreetingPreference).find(
    ([, value]) => value === greetingPreference
  )?.[0];
};

export const normalizeAstSignMailLanguage = (
  language: string | undefined
): string | undefined => {
  if (!language) {
    return undefined;
  }

  const normalizedLanguage = language.trim().toLowerCase();

  if (!normalizedLanguage) {
    return undefined;
  }

  return (
    CANONICAL_LANGUAGE_CODE_BY_VALUE[normalizedLanguage] ?? normalizedLanguage
  );
};

const resolveGreetingEligibility = (
  greetingPreference: GreetingPreference,
  astSignmailFlag: AstSignMailFlag | undefined
) => {
  // LD stores greeting allow-list keys like "SORENSON_GREETING", not enum values like "1".
  const greetingPreferenceKey =
    resolveGreetingPreferenceFlagKey(greetingPreference);

  return {
    greetingPreferenceKey,
    isGreetingAllowed:
      !!greetingPreferenceKey &&
      !!astSignmailFlag?.greetings?.includes(greetingPreferenceKey),
  };
};

const isCallLanguageAllowed = (
  callLanguageCode: string,
  astSignmailFlag: AstSignMailFlag | undefined
): boolean => {
  // Normalize both sides so "English"/"en" and "Spanish"/"es" compare equal.
  const normalizedCallLanguage = normalizeAstSignMailLanguage(callLanguageCode);
  const normalizedAllowedLanguages =
    astSignmailFlag?.allowedLanguages
      ?.map(normalizeAstSignMailLanguage)
      .filter((language): language is string => !!language) ?? [];

  return (
    !!normalizedCallLanguage &&
    normalizedAllowedLanguages.includes(normalizedCallLanguage)
  );
};

const getAstSignMailBlockingReasons = ({
  isFeatureFlagEnabled,
  isGreetingAllowed,
  isLanguageAllowed,
  isAstSignMailModeEnabled,
  isAllowedByInitialCallOnlyPolicy,
  isMailboxFull,
  isBlockedByEmergencyCall,
  isBlockedByEmergencyCallback,
}: AstSignMailEligibilityChecks): AstSignMailBlockingReason[] => {
  const blockingReasons: AstSignMailBlockingReason[] = [];

  if (!isFeatureFlagEnabled) {
    blockingReasons.push(AST_SIGN_MAIL_BLOCKING_REASON.FEATURE_FLAG_DISABLED);
  }
  if (!isGreetingAllowed) {
    blockingReasons.push(AST_SIGN_MAIL_BLOCKING_REASON.GREETING_NOT_ALLOWED);
  }
  if (!isLanguageAllowed) {
    blockingReasons.push(AST_SIGN_MAIL_BLOCKING_REASON.LANGUAGE_NOT_ALLOWED);
  }
  if (!isAstSignMailModeEnabled) {
    blockingReasons.push(AST_SIGN_MAIL_BLOCKING_REASON.CORE_MODE_DISABLED);
  }
  if (!isAllowedByInitialCallOnlyPolicy) {
    blockingReasons.push(
      AST_SIGN_MAIL_BLOCKING_REASON.INITIAL_CALL_ONLY_POLICY
    );
  }
  if (isMailboxFull) {
    blockingReasons.push(AST_SIGN_MAIL_BLOCKING_REASON.MAILBOX_FULL);
  }
  if (isBlockedByEmergencyCall) {
    blockingReasons.push(AST_SIGN_MAIL_BLOCKING_REASON.EMERGENCY_CALL);
  }
  if (isBlockedByEmergencyCallback) {
    blockingReasons.push(
      AST_SIGN_MAIL_BLOCKING_REASON.EMERGENCY_CALLBACK_DISABLED
    );
  }

  return blockingReasons;
};

/**
 * Resolves whether auto AST SignMail may run for a call.
 *
 * Every failed gate is included in `blockingReasons`; the call is eligible
 * only when that list is empty. When `routingContext` is omitted, call-specific
 * blockers default to false and the initial-call-only policy allows the call.
 */
export const resolveAstSignMailEligibility = (
  greetingPreference: GreetingPreference,
  callLanguageCode: string,
  astSignMailMode: number | undefined,
  routingContext?: AstSignMailRoutingContext
): AstSignMailEligibility => {
  const astSignmailFlag = FeatureFlagService.getInstance().flags.astSignmail as
    AstSignMailFlag | undefined;

  const { greetingPreferenceKey, isGreetingAllowed } =
    resolveGreetingEligibility(greetingPreference, astSignmailFlag);

  const isLanguageAllowed = isCallLanguageAllowed(
    callLanguageCode,
    astSignmailFlag
  );

  const isAstSignMailModeEnabled =
    astSignMailMode === AST_SIGNMAIL_MODE_ENABLED;

  const isInitialCallOnlyPolicyEnabled =
    astSignmailFlag?.initialCallOnly ?? false;

  const isAllowedByInitialCallOnlyPolicy =
    !isInitialCallOnlyPolicyEnabled ||
    !routingContext ||
    !routingContext.hasDeafConnected;

  const isMailboxFull = routingContext?.isMailboxFull ?? false;
  const emergencyCallbacksEnabled =
    astSignmailFlag?.emergencyCallbacksEnabled ?? false;
  const isBlockedByEmergencyCall = routingContext?.isEmergencyCall ?? false;
  const isBlockedByEmergencyCallback =
    (routingContext?.isEmergencyCallback ?? false) &&
    !emergencyCallbacksEnabled;

  const eligibilityChecks: AstSignMailEligibilityChecks = {
    isFeatureFlagEnabled: astSignmailFlag?.enabled ?? false,
    isGreetingAllowed,
    isLanguageAllowed,
    isAstSignMailModeEnabled,
    isAllowedByInitialCallOnlyPolicy,
    isMailboxFull,
    isBlockedByEmergencyCall,
    isBlockedByEmergencyCallback,
  };

  const blockingReasons = getAstSignMailBlockingReasons(eligibilityChecks);

  const isAstEligible = blockingReasons.length === 0;

  return {
    isAstEligible,
    blockingReasons,
    astSignmailFlag,
    greetingPreferenceKey,
    isGreetingAllowed,
    isLanguageAllowed,
    checks: eligibilityChecks,
  };
};
